import { CommonModule } from '@angular/common';
import { Component, effect, inject, Input, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { CustomValidators } from '../../../shared/validators/custom-validators';
import { SelectModule } from 'primeng/select';
import { ModalService } from '../../../services/modals/modal.service';
import { ApiService } from '../../../services/api.service';
import { ICountry } from '../../../models/ICountry';
import { IState } from '../../../models/IState';
import { ICreditCardRequest } from '../../../models/ICreditCardRequest';
import { AuthService } from '../../../services/authentication/auth.service';
import { RecaptchaLoaderService } from '../../../services/recaptcha-loader.service';
import { CreditCardSuccessfullyComponent } from './credit-card-successfully/credit-card-successfully.component';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { BidService } from '../../../services/bidding/bid.service';

@Component({
  selector: 'app-credit-card-information',
  imports: [ReactiveFormsModule, ButtonModule, CommonModule, FormsModule, SelectModule, ProgressSpinnerModule],
  templateUrl: './credit-card-information.component.html',
  providers: [DialogService],
  styleUrl: './credit-card-information.component.scss'
})
export class CreditCardInformationComponent implements OnInit, OnDestroy {

 isCommunity: boolean = false;

  modalService = inject(ModalService);
  apiService = inject(ApiService);
  authService = inject(AuthService);
  biddingService = inject(BidService);
  private readonly recaptchaLoader = inject(RecaptchaLoaderService);

  creditCardInfoForm: FormGroup

  expirationError!: string;
  apiResponseError: string | null = null;

  isLoading: boolean = false;

  countries: ICountry[] = [];
  states: IState[] = [];
  months: { label: string, value: string }[] = [];
  years: { label: string, value: string }[] = [];

  errorMessage: string | null = null;


  labelState!: string;
  labelPostalCode!: string;
  placeHolderState!: string;
  defaultCountry!: string;


  homePageSignal = this.apiService.homePageSignal;

  onCardAdded?: () => void;


  constructor(private fb: FormBuilder, public config: DynamicDialogConfig) {
    this.creditCardInfoForm = this.fb.group({
      cardHolder: ['', [CustomValidators.required('Card Holder Name')]],
      cardNumber: ['', [CustomValidators.required('Card Number'), CustomValidators.numeric('Card Number')]],
      expirationMonth: ['', CustomValidators.required('Expiration Date')],
      expirationYear: ['', CustomValidators.required('Expiration Date')],
      country: ['', CustomValidators.required],
      billingAddress: ['', CustomValidators.required('Address')],
      zipCode: ['', [CustomValidators.required('ZIP Code'), CustomValidators.zipCodeValidator('country')]],
      city: ['', CustomValidators.required('City')],
      state: ['', [CustomValidators.required(''), CustomValidators.atLeastOneLetter('Region')]],
    });

    effect(() => {
      const countries = this.apiService.countrySignal();
      if (countries)
        // console.log('countries', countries);
        this.countries = countries;

      const states = this.apiService.stateSignal();
      if (states && states.length > 0) {
        this.states = states;
        // this.creditCardInfoForm.get('state')?.setValue(this.states[0].abbreviation);
      } else {
        this.states = [];
        this.creditCardInfoForm.get('state')?.setValue('');
      }
    });
  }

  ngOnDestroy(): void {
    this.recaptchaLoader.unload();
  }

  ngOnInit(): void {
    this.recaptchaLoader.load();

    this.onCountries();

    console.log(this.isCommunity)


    if (this.homePageSignal()) {
      this.defaultCountry = this.homePageSignal()!.orgCountryCode;
    }

    this.creditCardInfoForm.get('country')?.setValue(this.defaultCountry);
    this.onLabelState(this.defaultCountry);
    this.onStates(this.defaultCountry);

    if (this.defaultCountry === 'US') {
      this.labelPostalCode = 'Zip Code'
    } else {
      this.labelPostalCode = 'Postal Code'

    }

    //country select event
    this.creditCardInfoForm.get('country')?.valueChanges.subscribe(country => {
      this.onLabelState(country);
      this.onStates(country);

      // Reset fields and clear validators 
      this.creditCardInfoForm.get('state')?.clearValidators();
      this.creditCardInfoForm.get('zipCode')?.clearValidators();
      this.creditCardInfoForm.get('state')?.reset();
      this.creditCardInfoForm.get('zipCode')?.reset();


      this.creditCardInfoForm.get('state')?.setValidators([
        CustomValidators.required(this.labelState),
        CustomValidators.atLeastOneLetter(this.labelState)
      ]);
      this.creditCardInfoForm.get('state')?.updateValueAndValidity();


      this.labelPostalCode = country === 'US' ? 'Zip Code' : 'Postal Code';

      this.creditCardInfoForm.get('zipCode')?.setValidators([
        CustomValidators.required(this.labelPostalCode),
        CustomValidators.zipCodeValidator(this.labelPostalCode)
      ]);
      this.creditCardInfoForm.get('zipCode')?.updateValueAndValidity();
    });


    this.loadMonths();
    this.loadYears();

    //execute loadCards in itemComponent
    this.onCardAdded = this.config.data?.onCardAdded;

    this.isCommunity = this.config.data?.isCommunity;

  }

  onLabelState(country: string) {
    switch (country) {
      case 'US':
        this.labelState = 'State'
        this.placeHolderState = 'Select State'
        break;
      case 'CA':
        this.labelState = 'Province'
        this.placeHolderState = 'Select Province'
        break;
      case 'AU':
        this.labelState = 'State'
        this.placeHolderState = 'Select State'
        break;
      default:
        this.labelState = 'Region'
        break;
    }
  }

  getFieldError(field: string): string | null {
    const control = this.creditCardInfoForm.get(field);
    if (control && control.errors && (control.dirty || control.touched)) {
      const firstErrorKey = Object.keys(control.errors)[0];
      return control.errors[firstErrorKey];
    }
    return null;
  }


  onSkip() {
    this.modalService.closeModal('credit-card');

  }

  loadMonths() {
    this.months = Array.from({ length: 12 }, (_, i) => {
      const monthNumber = (i + 1).toString().padStart(2, '0');
      return { label: monthNumber, value: monthNumber };
    });
  }

  loadYears() {
    const currentYear = new Date().getFullYear();
    const yearsRange = 10;

    this.years = Array.from({ length: yearsRange }, (_, i) => {
      const year = currentYear + i;
      return { label: year.toString(), value: year.toString() };
    });
  }

  onCountries() {
    if (typeof window !== 'undefined') {
      this.apiService.getCountries().subscribe();
    }
  }

  onStates(countrySelected: string) {
    if (typeof window !== 'undefined') {
      const form = this.creditCardInfoForm.value;
      // const countrySelected = form.get('country')?.value;

      this.apiService.getStates(countrySelected).subscribe();

    }
  }

  onCardNumberInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const cleanValue = input.value.replace(/\s+/g, '');
    this.creditCardInfoForm.get('cardNumber')?.setValue(cleanValue, { emitEvent: false });
    if (this.apiResponseError) {
      this.apiResponseError = null;
    }
  }

  async onSubmit() {

    this.apiResponseError = '';

    if (this.creditCardInfoForm.invalid) {

      this.creditCardInfoForm.markAllAsTouched();
      return;
    }

    const form = this.creditCardInfoForm.value;

    const month = Number(form.expirationMonth);
    const year = Number(form.expirationYear);

    const expirationDate = new Date(year, month, 0, 23, 59, 59);
    const now = new Date();

    if (expirationDate < now) {

      this.expirationError = 'Expiration date invalid';
      return;
    } else {
      this.expirationError = '';
    }


    const creditCardObj: ICreditCardRequest = {
      number: form.cardNumber,
      token: '123',
      name: form.cardHolder,
      expMonth: form.expirationMonth,
      expYear: form.expirationYear,
      country: form.country,
      postalCode: form.zipCode,
      region: form.state,
      city: form.city,
      address1: form.billingAddress,
      address2: '',
      defaultCard: false,
    }

    // console.log(creditCardObj);
    this.isLoading = true;
    (await this.authService.addCreditCard(creditCardObj)).subscribe({
      next: res => {
        // console.log('successful card')
        this.isLoading = false;
        this.onCardAdded?.();
        this.modalService.closeModal('credit-card');
        this.modalService.openModal('credit-card-successfully', CreditCardSuccessfullyComponent);
        this.loadCards();// loads cards and fill out the signal

      },
      error: (err) => {
        this.isLoading = false;
        console.log('error', err);
        this.apiResponseError = err.message;
      }

    });
  }

  loadCards() {

    this.authService.getCreditCard().subscribe(({
      next: (response) => {
        // console.log('credit cards', response);

      }, error: (error) => {
        // console.error('Error getting token:', error);
      }
    }));
  }
}
